import type { Cell } from "@highmarch/domain";

/**
 * What a hole sheet — the chasm floor or the lava melt — needs from the ground: which cells it covers, where it must
 * reach in under the ground, how high each cell sits, and the ground's own lateral wander.
 */
export interface HoleSheetSpec {
  readonly cells: readonly Cell[];
  /** True for a cell this sheet covers. */
  readonly has: (x: number, z: number) => boolean;
  /** True where the cell across an edge is ground: the sheet runs a skirt in under that cell's wall. */
  readonly skirtToward: (x: number, z: number) => boolean;
  /** Quads per cell side. */
  readonly sub: number;
  /** How far the skirt runs past the cell edge, in world units. */
  readonly skirt: number;
  /** The sheet's height at a cell (its centre value, the one the wall's foot is built down to). */
  readonly levelOf: (x: number, z: number) => number;
  /** The lateral wander at an unwarped point. Must be smooth: a jump between neighbouring vertices can fold a quad. */
  readonly warp: (x: number, z: number) => [number, number];
  /** Small vertical roughness at an unwarped point. */
  readonly bump: (x: number, z: number) => number;
}

/** A built sheet: warped positions, the unwarped XZ of every vertex (for world-space UVs), and upward-facing triangles. */
export interface HoleSheet {
  readonly position: number[];
  readonly xz: number[];
  readonly index: number[];
}

const Sides: readonly [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/**
 * One welded, watertight sheet across every cell of a hole, with skirts under the surrounding walls.
 *
 * The floor and the melt used to be one loose patch per cell at the cell's own height. Neighbouring patches then met
 * at different heights (the hills run under a gorge), and the ground's wander is not continuous from cell to cell —
 * it is 3.1× at a lip cell and 1× inside the hole — so patch edges and the wall's foot drifted apart by more than the
 * patches overlapped. Through every such crack the camera saw the clear colour: the white slivers along a gorge's
 * foot and the white triangles in a lava ribbon. Here every lattice point is ONE vertex (its height the mean of the
 * cells that share it, its wander one smooth function), and every edge that meets a wall carries a flat skirt in
 * under the ground, far enough past any difference in wander that the wall's foot always stands on floor.
 */
export function buildHoleSheet(spec: HoleSheetSpec): HoleSheet {
  const N = spec.sub;
  const position: number[] = [], xz: number[] = [], index: number[] = [];
  const ids = new Map<string, number>();
  const emit = (key: string, ux: number, uz: number, ax: number, az: number, y: number): number => {
    const hit = ids.get(key);
    if (hit !== undefined) return hit;
    const i = xz.length / 2;
    // a skirt vertex takes its anchor's wander, so the strip leaves the edge squarely instead of wandering back in
    const [dx, dz] = spec.warp(ax, az);
    position.push(ux + dx, y, uz + dz);
    xz.push(ux, uz);
    ids.set(key, i);
    return i;
  };
  // the cells along one axis whose closed span holds lattice coordinate g: two on a cell boundary, one inside a cell
  const owners = (g: number): number[] => (g % N === 0 ? [g / N - 1, g / N] : [Math.floor(g / N)]);
  const levelAt = (gx: number, gz: number): number => {
    let sum = 0, n = 0;
    for (const x of owners(gx)) for (const z of owners(gz)) if (spec.has(x, z)) { sum += spec.levelOf(x, z); n++; }
    return sum / n;
  };
  const world = (g: number): number => g / N - 0.5;
  const lattice = (gx: number, gz: number): number => {
    const ux = world(gx), uz = world(gz);
    return emit(`${gx}|${gz}`, ux, uz, ux, uz, levelAt(gx, gz) + spec.bump(ux, uz));
  };
  const outer = (gx: number, gz: number, ox: number, oz: number): number => {
    const ux = world(gx), uz = world(gz);
    const y = position[lattice(gx, gz) * 3 + 1];
    return emit(`${gx}|${gz}|${ox}|${oz}`, ux + ox * spec.skirt, uz + oz * spec.skirt, ux, uz, y);
  };
  // wound so the face points up whatever order the caller lists the corners in (the materials are single-sided)
  const tri = (a: number, b: number, c: number): void => {
    const abx = xz[b * 2] - xz[a * 2], abz = xz[b * 2 + 1] - xz[a * 2 + 1];
    const acx = xz[c * 2] - xz[a * 2], acz = xz[c * 2 + 1] - xz[a * 2 + 1];
    if (abz * acx - abx * acz >= 0) index.push(a, b, c); else index.push(a, c, b);
  };
  for (const c of spec.cells) {
    // lattice coordinate g is world g / N - 0.5, so cell c's low corner (c - 0.5) is lattice c * N
    const gx0 = c.x * N, gz0 = c.y * N;
    for (let iz = 0; iz < N; iz++) {
      for (let ix = 0; ix < N; ix++) {
        const a = lattice(gx0 + ix, gz0 + iz), b = lattice(gx0 + ix + 1, gz0 + iz);
        const d = lattice(gx0 + ix, gz0 + iz + 1), e = lattice(gx0 + ix + 1, gz0 + iz + 1);
        tri(a, d, b); tri(b, d, e);
      }
    }
    const open: [number, number][] = Sides.filter(([dx, dz]) => spec.skirtToward(c.x + dx, c.y + dz));
    for (const [dx, dz] of open) {
      // the edge on that side, walked along its length
      const along: [number, number][] = [];
      for (let s = 0; s <= N; s++) {
        along.push(dx !== 0 ? [gx0 + (dx > 0 ? N : 0), gz0 + s] : [gx0 + s, gz0 + (dz > 0 ? N : 0)]);
      }
      for (let s = 0; s < N; s++) {
        const [px, pz] = along[s], [qx, qz] = along[s + 1];
        const p = lattice(px, pz), q = lattice(qx, qz), po = outer(px, pz, dx, dz), qo = outer(qx, qz, dx, dz);
        tri(p, q, po); tri(q, qo, po);
      }
    }
    // where two skirts leave the same corner at right angles, close the square between them
    for (const [ax, az] of open) {
      for (const [bx, bz] of open) {
        if (ax === 0 || bz === 0) continue;                    // pair each x side with each z side once
        const gx = gx0 + (ax > 0 ? N : 0), gz = gz0 + (bz > 0 ? N : 0);
        const ux = world(gx), uz = world(gz);
        const corner = lattice(gx, gz);
        const diag = emit(`${gx}|${gz}|${ax}|${bz}|d`, ux + ax * spec.skirt, uz + bz * spec.skirt, ux, uz, position[corner * 3 + 1]);
        tri(corner, outer(gx, gz, ax, 0), diag);
        tri(corner, diag, outer(gx, gz, 0, bz));
      }
    }
  }
  return { position, xz, index };
}
